import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@core/components/ui';

// `label` is an i18n key, resolved with `t()` where the button renders.
const REVIEW_COMMANDS = [
  { label: 'dataroom.collabora.previous', icon: 'mdi:chevron-up', command: '.uno:PreviousTrackedChange' },
  { label: 'dataroom.collabora.next', icon: 'mdi:chevron-down', command: '.uno:NextTrackedChange' },
  { label: 'dataroom.collabora.accept', icon: 'mdi:check', command: '.uno:AcceptTracedChange', tone: 'accept', saveAfter: true },
  { label: 'dataroom.collabora.reject', icon: 'mdi:close', command: '.uno:RejectTracedChange', tone: 'reject', saveAfter: true },
  { label: 'dataroom.collabora.acceptAll', icon: 'mdi:check-all', command: '.uno:AcceptAllTrackedChanges', tone: 'accept', saveAfter: true, saveDelayMs: 800, reloadDelayMs: 2000, resolvesAll: true },
  { label: 'dataroom.collabora.rejectAll', icon: 'mdi:close-box-multiple-outline', command: '.uno:RejectAllTrackedChanges', tone: 'reject', saveAfter: true, saveDelayMs: 800, reloadDelayMs: 2000, resolvesAll: true },
  { label: 'dataroom.collabora.manage', icon: 'mdi:format-list-checks', command: '.uno:AcceptTrackedChanges' },
];

/**
 * Collabora Online viewer for Office documents (iframe-based).
 */
export function CollaboraViewer({ wopiSrc, accessToken, collaboraUrl, reviewMode = false }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reviewResolution, setReviewResolution] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);
  const collaboraOrigin = useMemo(() => new URL(collaboraUrl).origin, [collaboraUrl]);

  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams({
      WOPISrc: wopiSrc,
      access_token: accessToken,
    });
    if (!reviewMode) {
      params.set('compact', 'true');
      params.set('NotebookbarTabStyle', 'Collapsed');
    }
    return `${collaboraUrl}/browser/dist/cool.html?${params.toString()}`;
  }, [accessToken, collaboraUrl, reviewMode, wopiSrc]);

  const postToEditor = useCallback((messageId, values = {}) => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        MessageId: messageId,
        SendTime: Date.now(),
        Values: values,
      },
      collaboraOrigin,
    );
  }, [collaboraOrigin]);

  const sendUnoCommand = useCallback((command) => {
    postToEditor('Send_UNO_Command', { Command: command });
  }, [postToEditor]);

  const sendReviewCommand = useCallback((item) => {
    sendUnoCommand(item.command);
    if (item.saveAfter) {
      window.setTimeout(() => sendUnoCommand('.uno:Save'), item.saveDelayMs ?? 250);
    }
    if (item.resolvesAll) {
      setReviewResolution({
        status: 'saving',
        label: item.tone === 'reject' ? 'dataroom.collabora.rejectingAll' : 'dataroom.collabora.acceptingAll',
      });
      window.setTimeout(() => {
        setIsLoading(true);
        setReloadKey((key) => key + 1);
        setReviewResolution({
          status: 'resolved',
          label: item.tone === 'reject' ? 'dataroom.collabora.allRejected' : 'dataroom.collabora.allAccepted',
        });
      }, item.reloadDelayMs ?? 1200);
    }
  }, [sendUnoCommand]);

  useEffect(() => {
    if (!reviewMode) return undefined;

    const handleMessage = (event) => {
      if (event.origin !== collaboraOrigin) return;
      const { MessageId: messageId, Values: values = {} } = event.data || {};
      if (messageId !== 'App_LoadingStatus') return;

      if (values.Status === 'Initialized' || values.Status === 'Frame_Ready') {
        postToEditor('Host_PostmessageReady');
      }
      if (values.Status === 'Document_Loaded') {
        postToEditor('Send_UNO_Command', {
          Command: '.uno:ShowTrackedChanges',
          Args: {
            ShowTrackedChanges: {
              type: 'boolean',
              value: true,
            },
          },
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [collaboraOrigin, postToEditor, reviewMode]);

  useEffect(() => {
    setReviewResolution(null);
    setReloadKey(0);
    setIsLoading(true);
  }, [accessToken, wopiSrc]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-red-500">
        <Icon icon="mdi:alert-circle" className="size-12 mb-2" />
        <p className="text-sm">{t('dataroom.collabora.loadError')}</p>
      </div>
    );
  }

  return (
    <div className="h-full relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white">
          <Spinner size="lg" />
        </div>
      )}
      {reviewMode && (
        <div className="h-10 border-b border-[var(--color-border)] bg-white flex items-center gap-1 px-2">
          {reviewResolution ? (
            <div className="inline-flex items-center gap-2 px-2 text-xs font-medium text-[var(--color-text-secondary)]">
              {reviewResolution.status === 'saving' ? (
                <Spinner size="sm" />
              ) : (
                <Icon icon="mdi:check-circle" className="size-3.5 text-emerald-600" />
              )}
              <span>{t(reviewResolution.label)}</span>
            </div>
          ) : (
            REVIEW_COMMANDS.map((item) => (
              <button
                key={item.command}
                type="button"
                onClick={() => sendReviewCommand(item)}
                className={`cursor-pointer inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                  item.tone === 'accept'
                    ? 'text-emerald-700 hover:bg-emerald-50'
                    : item.tone === 'reject'
                      ? 'text-red-700 hover:bg-red-50'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)]'
                }`}
                title={t(item.label)}
              >
                <Icon icon={item.icon} className="size-3.5" />
                <span>{t(item.label)}</span>
              </button>
            ))
          )}
        </div>
      )}
      <iframe
        key={reloadKey}
        ref={iframeRef}
        src={iframeSrc}
        className={`w-full border-0 ${reviewMode ? 'h-[calc(100%-2.5rem)]' : 'h-full'}`}
        title={t('dataroom.collabora.iframeTitle')}
        allow="clipboard-read; clipboard-write"
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}

CollaboraViewer.propTypes = {
  wopiSrc: PropTypes.string.isRequired,
  accessToken: PropTypes.string.isRequired,
  collaboraUrl: PropTypes.string.isRequired,
  reviewMode: PropTypes.bool,
};
